import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Warning, ArrowUpRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const gap = (job) => {
  const plan = job.machine_plan || [];
  if (!plan.length) return "No machines planned";
  if (!plan.some((r) => (r.operator_ids || []).length)) return "No operators allocated";
  if (!plan.some((r) => Number(r.planned_hours) > 0)) return "No planned hours set";
  return null;
};

export default function AllocationReminder() {
  const navigate = useNavigate();
  const { data: jobs = [] } = useQuery({
    queryKey: ["entity", "jobs"],
    queryFn: async () => (await api.get("/jobs")).data,
  });

  const rows = jobs
    .filter((j) => j.job_status === "Active")
    .map((j) => ({ ...j, issue: gap(j) }))
    .filter((j) => j.issue);

  if (rows.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/60 shadow-none" data-testid="allocation-reminder">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-amber-100">
        <Warning className="h-5 w-5 text-amber-600" weight="fill" />
        <h3 className="font-semibold text-amber-900">
          Allocation Needed — {rows.length} active job{rows.length === 1 ? "" : "s"} without a machine plan
        </h3>
      </div>
      <ul className="divide-y divide-amber-100">
        {rows.map((j) => (
          <li key={j.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-5 py-3"
            data-testid={`allocation-gap-${j.code}`}>
            <span className="text-sm min-w-0">
              <span className="font-mono text-xs text-slate-500">{j.code}</span>{" "}
              <span className="font-medium text-slate-900">{j.job_name}</span>
              <span className="text-slate-500"> · {j.client_name}</span>
              <Badge variant="outline" className="ml-2 bg-white text-amber-700 border-amber-200">{j.issue}</Badge>
              <span className="block text-xs text-slate-500 mt-0.5">
                Timers can still be started, but hours will not land against a planned machine until this is set.
              </span>
            </span>
            <Button size="sm" variant="outline" className="h-7 shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`allocation-fix-${j.code}`}>
              Set machine plan <ArrowUpRight className="h-4 w-4 ml-1" weight="bold" />
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
